import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, X, Layers } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { compilationsApi, CompilationListRow, CreateProjectInput } from '@/lib/compilations';
import { forecastsApi } from '@/lib/forecasts';
import { membersApi, memberLabel } from '@/lib/members';
import { isPlanGateError } from '@/lib/planGate';
import { UpgradeWall } from '@/components/app/UpgradeWall';

const today = new Date().toISOString().slice(0, 10);
const oneYearAgo = `${Number(today.slice(0, 4)) - 1}${today.slice(4)}`;
const STATUS_PILL: Record<string, string> = {
  draft: 'bg-ink-100 text-ink-600', collecting: 'bg-amber-50 text-amber-700',
  review: 'bg-blue-50 text-blue-700', finalized: 'bg-emerald-50 text-emerald-700',
};

export function CompilationPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const projects = useQuery({ queryKey: ['compilations', wsId], queryFn: () => compilationsApi.list(wsId!), enabled: !!wsId, retry: false });
  const planGated = projects.error && isPlanGateError(projects.error);

  return (
    <AppShell title={t('compilation.title', { defaultValue: 'Budget compilation' })} subtitle={t('compilation.subtitle', { defaultValue: 'Collect budgets from each department, then finalize a version.' })}>
      <main className="mx-auto max-w-5xl px-6 py-10 space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('compilation.title', { defaultValue: 'Budget compilation' })} <span className="text-muted-foreground text-lg">編成プロジェクト</span></h1>
            <p className="mt-1 text-ink-600">{t('compilation.subtitle', { defaultValue: 'Collect budgets from each department, then finalize a version.' })}</p>
          </div>
          {!planGated && <button onClick={() => setShowForm((v) => !v)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"><Plus className="h-4 w-4" /> {t('compilation.new_project', { defaultValue: 'New project' })}</button>}
        </div>

        {planGated && wsId && <UpgradeWall wsId={wsId} requiredTier="growth" featureName={t('compilation.feature_name', { defaultValue: 'Budget compilation' })} />}

        {showForm && wsId && <NewProjectForm wsId={wsId} onClose={() => setShowForm(false)} onDone={() => { qc.invalidateQueries({ queryKey: ['compilations', wsId] }); setShowForm(false); }} t={t} />}

        {!planGated && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {projects.isLoading ? <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
              : (projects.data ?? []).length === 0 ? (
                <div className="sm:col-span-2 rounded-md border-2 border-dashed border-border bg-white p-10 text-center">
                  <Layers className="h-6 w-6 text-ink-300 mx-auto mb-2" />
                  <p className="text-ink-700 font-medium">{t('compilation.empty', { defaultValue: 'No compilation projects yet.' })}</p>
                </div>
              ) : (projects.data ?? []).map((p: CompilationListRow) => (
                <Link key={p.id} to={`/app/workspace/${wsId}/compilation/${p.id}`} className="rounded-md border border-border bg-white p-4 hover:border-emerald-400 transition-colors block">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-ink-900 truncate">{p.name}</p>
                      <p className="text-xs text-muted-foreground num">{p.target_scenario} · {p.base_scenario} · {p.period_start}→{p.period_end}</p>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wide font-semibold rounded px-1.5 py-0.5 ${STATUS_PILL[p.status]}`}>{p.status}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-[11px] num">
                    <span className="rounded px-1.5 py-0.5 bg-emerald-50 text-emerald-700">{p.approved} 承認済み</span>
                    <span className="rounded px-1.5 py-0.5 bg-blue-50 text-blue-700">{p.submitted} 提出済み</span>
                    <span className="rounded px-1.5 py-0.5 bg-ink-100 text-ink-600">{p.total_units} 対象部署</span>
                  </div>
                </Link>
              ))}
          </div>
        )}
      </main>
    </AppShell>
  );
}

type TFn = (k: string, o?: Record<string, unknown>) => string;

function NewProjectForm({ wsId, onClose, onDone, t }: { wsId: string; onClose: () => void; onDone: () => void; t: TFn }) {
  const { data: scenarios = [] } = useQuery({ queryKey: ['scenarios', wsId], queryFn: () => forecastsApi.scenarios(wsId) });
  const { data: members = [] } = useQuery({ queryKey: ['members', wsId], queryFn: () => membersApi.list(wsId) });
  const scenarioOpts = useMemo(() => Array.from(new Set(['budget', 'actual', ...scenarios.map((s) => s.scenario)])), [scenarios]);

  const [name, setName] = useState('');
  const [baseScenario, setBaseScenario] = useState('budget');
  const [version, setVersion] = useState('1');
  const [periodStart, setPeriodStart] = useState(oneYearAgo);
  const [periodEnd, setPeriodEnd] = useState(today);
  const [dimensionKey, setDimensionKey] = useState('dept');
  const [units, setUnits] = useState<{ unitKey: string; unitLabel: string; assigneeUserId: string }[]>([{ unitKey: '', unitLabel: '', assigneeUserId: '' }]);
  const [err, setErr] = useState<string | null>(null);
  const input = 'w-full rounded-lg border border-border bg-white px-3 py-2 text-sm';

  const save = useMutation({
    mutationFn: async () => {
      const body: CreateProjectInput = { name: name.trim() || 'Budget compilation', baseScenario, targetScenario: `budget:v${version}`, dimensionKey: dimensionKey.trim() || 'dept', periodStart, periodEnd };
      const proj = await compilationsApi.create(wsId, body);
      const valid = units.filter((u) => u.unitKey.trim());
      if (valid.length) await compilationsApi.addUnits(wsId, proj.id, valid.map((u) => ({ unitKey: u.unitKey.trim(), unitLabel: u.unitLabel.trim() || u.unitKey.trim(), assigneeUserId: u.assigneeUserId || null })));
      return proj;
    },
    onSuccess: onDone,
    onError: (e) => setErr(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) ?? (e as Error).message),
  });

  return (
    <div className="rounded-md border border-border bg-white p-5 space-y-4">
      <h3 className="text-sm font-semibold text-ink-700">{t('compilation.new_project', { defaultValue: 'New project' })}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Lbl label={t('compilation.project_name', { defaultValue: 'Name' })}><input value={name} onChange={(e) => setName(e.target.value)} className={input} placeholder="FY2027 Budget" /></Lbl>
        <Lbl label={t('compilation.target_version', { defaultValue: 'Budget version' })}><div className="flex items-center gap-1"><span className="num text-sm text-muted-foreground">budget:v</span><input value={version} onChange={(e) => setVersion(e.target.value.replace(/\D/g, ''))} className={`num ${input} max-w-[80px]`} /></div></Lbl>
        <Lbl label={t('compilation.base_scenario', { defaultValue: 'Seed / diff from' })}><select value={baseScenario} onChange={(e) => setBaseScenario(e.target.value)} className={input}>{scenarioOpts.map((s) => <option key={s} value={s}>{s}</option>)}</select></Lbl>
        <Lbl label={t('compilation.dimension', { defaultValue: 'Department dimension key' })}><input value={dimensionKey} onChange={(e) => setDimensionKey(e.target.value)} className={`num ${input}`} placeholder="dept" /></Lbl>
        <Lbl label={t('pl.period_start', { defaultValue: 'From' })}><input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className={`num ${input}`} /></Lbl>
        <Lbl label={t('pl.period_end', { defaultValue: 'To' })}><input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className={`num ${input}`} /></Lbl>
      </div>
      <div>
        <span className="block text-xs font-semibold text-ink-700 mb-1">{t('compilation.departments', { defaultValue: 'Departments + owners' })}</span>
        <div className="space-y-2">
          {units.map((u, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={u.unitKey} onChange={(e) => setUnits((us) => us.map((x, j) => j === i ? { ...x, unitKey: e.target.value } : x))} placeholder={t('compilation.dept_key', { defaultValue: 'key (e.g. Sales)' })} className={`num ${input}`} />
              <input value={u.unitLabel} onChange={(e) => setUnits((us) => us.map((x, j) => j === i ? { ...x, unitLabel: e.target.value } : x))} placeholder={t('compilation.dept_label', { defaultValue: 'label' })} className={input} />
              <select value={u.assigneeUserId} onChange={(e) => setUnits((us) => us.map((x, j) => j === i ? { ...x, assigneeUserId: e.target.value } : x))} className={`${input} max-w-[200px]`}>
                <option value="">{t('compilation.assignee', { defaultValue: 'assignee…' })}</option>
                {members.map((m) => <option key={m.id} value={m.id}>{memberLabel(m)}</option>)}
              </select>
              <button onClick={() => setUnits((us) => us.filter((_, j) => j !== i))} className="text-ink-400 hover:text-warn-600 p-1"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={() => setUnits((us) => [...us, { unitKey: '', unitLabel: '', assigneeUserId: '' }])} className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> {t('compilation.add_dept', { defaultValue: 'Add department' })}</button>
        </div>
      </div>
      {err && <p className="text-xs text-warn-700">{err}</p>}
      <div className="flex gap-2">
        <button onClick={() => save.mutate()} disabled={save.isPending} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60">{save.isPending ? t('common.loading') : t('compilation.create', { defaultValue: 'Create project' })}</button>
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-sm text-ink-700 hover:bg-foreground/[0.04]">{t('common.cancel')}</button>
      </div>
    </div>
  );
}

function Lbl({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="block text-xs font-semibold text-ink-700 mb-1">{label}</span>{children}</label>;
}
